'use client'

import Typography from '@mui/material/Typography'
import { ForwardedRef, forwardRef, MouseEventHandler, ReactElement } from 'react'
import { tss } from 'tss-react/mui'
import Avatar from './Avatar'

const useStyles = tss.create(({ theme }) => ({
    root: {
        display: 'flex',

        '&:hover': {
            textDecoration: 'none',
        },
    },
    avatarWrapper: {
        position: 'relative',
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(0.5),
        borderRadius: '50%',
        marginRight: theme.spacing(-3.5),
    },
    moreText: {
        position: 'relative',
        height: '48px',
        width: '48px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.background.paper,
        border: `${theme.spacing(0.5)} solid ${theme.palette.divider}`,
        color: theme.palette.text.primary,
    },
}))

type AvatarStackProps = {
    avatars: string[]
    max?: number
    moreOverride?: string
    onClick?: MouseEventHandler<HTMLDivElement>
    className?: string
}

const AvatarStack = forwardRef(function AvatarStackRef(
    { avatars, max = 3, moreOverride, className, ...props }: AvatarStackProps,
    ref?: ForwardedRef<HTMLDivElement>
): ReactElement {
    const more = avatars.length > max ? avatars.length - max + 1 : 0
    const avatarsToShow = more ? avatars.slice(0, max - 1) : avatars
    const { classes, cx } = useStyles()

    return (
        <div {...props} ref={ref} className={cx(classes.root, className)}>
            {avatarsToShow.map((avatarImg) => (
                <div key={avatarImg} className={classes.avatarWrapper}>
                    <Avatar src={avatarImg} size="medium" />
                </div>
            ))}
            {moreOverride || more ? (
                <Typography component="div" variant="subtitle1" className={classes.moreText}>
                    {moreOverride ?? `+${more}`}
                </Typography>
            ) : null}
        </div>
    )
})

export default AvatarStack
